import type { IDetectMetricResult } from '~/modules/analytics/types';
import type { IDeviceTypeCount } from '../types/device-type-count';
import type { IScrollMapInfo } from '@gem-sdk/heatmap-react';
import type { GTooltipCardData } from '~/presentation/ui';

import {
  getTotalClickedMetric,
  getTotalRevenueMetric,
  getAverageTimeOnPageMetric,
  getTotalPageViewedMetric,
  getTotalOrderMetric,
  getAverageScrollDepthMetric,
  getEngagedSessionMetric,
} from '../helpers/metric';
import { useHeatmapDataContext, useHeatmapSettingContext } from '@gem-sdk/heatmap-react';
import { formatMetricByFormatter, useAnalyticMetricTooltip } from '~/modules/analytics';
import { EAnalyticMetricKey } from '~/modules/analytics/types';
import { useConvertMoneyByCurrency } from '~/modules/shopify';
import { EDeviceType, EHeatmapType } from '../types';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

const DEFAULT_DATA: IDeviceTypeCount = {
  deviceType: EDeviceType.Desktop,
  clickTypes: [],
  totalVisitors: 0,
  totalClicks: 0,
  totalPageViewed: 0,
  totalPageViewedHaveTimeOnPage: 0,
  totalTimeOnPage: 0,
  totalSessions: 0,
  engagedSessions: 0,
  avgScrollDepth: 0,
  totalSessionsThatCompletedCheckout: 0,
  totalRevenue: 0,
};

const buildMetric = (data: IDeviceTypeCount) => ({
  totalClicked: data.totalClicks,
  totalPageViewed: data.totalPageViewed,
  totalPageViewedHaveTimeOnPage: data.totalPageViewedHaveTimeOnPage,
  totalTimeOnPage: data.totalTimeOnPage,
  totalOrders: data.totalSessionsThatCompletedCheckout,
  totalRevenue: data.totalRevenue,
  totalSessions: data.totalSessions,
  engagedSessions: data.engagedSessions,
  totalSessionsThatCompletedCheckout: data.totalSessionsThatCompletedCheckout,
  avgScrollDepth: data.avgScrollDepth,
});

interface IMetricSummaryConfig {
  key: EAnalyticMetricKey;
  label: string;
  getMetric: (data: IDeviceTypeCount) => IDetectMetricResult;
}
export interface IMetricSummaryItem {
  label: string;
  value?: string;
  tooltip?: GTooltipCardData;
}

interface IHeatmapMetricSummaryProps {
  data?: IDeviceTypeCount;
  width?: GTooltipCardData['width'];
}

interface IHeatmapMetricSummaryResult {
  summaryItems: IMetricSummaryItem[];
  summaryCount: number;
}

export const useHeatmapMetricSummary = (
  props?: IHeatmapMetricSummaryProps,
): IHeatmapMetricSummaryResult => {
  const heatmapType = useHeatmapSettingContext((s) => s.heatmapType);
  const scrollMapInfo = useHeatmapDataContext((s) => s.dataInfo?.scrollMapInfo);

  const { data } = props ?? {};
  const { t } = useTranslation();
  const { getTextPrice } = useConvertMoneyByCurrency();
  const { getAnalyticTooltip } = useAnalyticMetricTooltip({ width: props?.width ?? 'wide' });

  const configs: IMetricSummaryConfig[] = useMemo(() => {
    const scrollConfigs: IMetricSummaryConfig[] = [
      {
        key: EAnalyticMetricKey.AVG_SCROLL_DEPTH,
        label: t('Avg scroll depth'),
        getMetric: (d) => getAverageScrollDepthMetric({ metric: buildMetric(d) }),
      },
    ];

    return [
      {
        key: EAnalyticMetricKey.REVENUE,
        label: t('Revenue'),
        getMetric: (d) => getTotalRevenueMetric({ metric: buildMetric(d) }),
      },
      {
        key: EAnalyticMetricKey.ORDERS,
        label: t('Orders'),
        getMetric: (d) => getTotalOrderMetric({ metric: buildMetric(d) }),
      },
      {
        key: EAnalyticMetricKey.PAGE_VIEWS,
        label: t('Pageviews'),
        getMetric: (d) => getTotalPageViewedMetric({ metric: buildMetric(d) }),
      },
      {
        key: EAnalyticMetricKey.CLICKS,
        label: t('Total clicks'),
        getMetric: (d) => getTotalClickedMetric({ metric: buildMetric(d) }),
      },
      {
        key: EAnalyticMetricKey.AVG_TIME,
        label: t('Avg time on page'),
        getMetric: (d) => getAverageTimeOnPageMetric({ metric: buildMetric(d) }),
      },
      ...(heatmapType === EHeatmapType.Scroll ? scrollConfigs : []),
      {
        key: EAnalyticMetricKey.ENGAGED_SESSIONS_DEVICE,
        label: t('Engaged sessions'),
        getMetric: (d) => getEngagedSessionMetric({ metric: buildMetric(d) }),
      },
    ];
  }, [heatmapType, t]);

  const summaryItems: IMetricSummaryItem[] = useMemo(() => {
    const mappedData = mappingDataMetric({ heatmapType, data, scrollMapInfo });
    return configs.map(({ key, label, getMetric }) => {
      const metricResult = getMetric(mappedData);

      return {
        label,
        value: metricResult
          ? formatMetricByFormatter({
              value: metricResult.value ?? 0,
              formatter: metricResult.formatter,
              getTextPrice,
            })
          : undefined,
        tooltip: getAnalyticTooltip(key),
      };
    });
  }, [heatmapType, data, scrollMapInfo, configs, getTextPrice, getAnalyticTooltip]);

  return { summaryItems, summaryCount: configs.length };
};

// TODO: Remove this after backend update calculate metric by mode (direct/indirect)
const mappingDataMetric = (options: {
  heatmapType?: EHeatmapType;
  data?: IDeviceTypeCount;
  scrollMapInfo?: IScrollMapInfo;
}): IDeviceTypeCount => {
  const { heatmapType, data, scrollMapInfo } = options;

  if (!data) return DEFAULT_DATA;

  if (heatmapType === EHeatmapType.Click) return data;
  if (heatmapType === EHeatmapType.Scroll) {
    return {
      ...data,
      totalRevenue: scrollMapInfo?.totalRevenue ?? 0,
      totalSessionsThatCompletedCheckout: scrollMapInfo?.totalOrders ?? 0,
      totalSessions: scrollMapInfo?.totalSessions ?? 0,
    };
  }

  return data;
};
